/**
 * Spanish — the café's own voice, and the shape every other dictionary must
 * match. `Dictionary` is derived from this object, so adding a key here turns
 * the other fourteen files red until they carry it too.
 *
 * The marketing lines are deliberately idiomatic rioplatense ("calidez de
 * barrio", "temperamento porteño"). Translations carry the meaning rather than
 * the words; nothing else would survive the trip.
 */
export const es = {
  language: {
    /** Accessible name of the flag button. */
    choose: 'Elegir idioma',
    current: (name: string) => `Idioma: ${name}`,
  },

  nav: {
    signIn: 'Ingresar',
    myProfile: 'Mi perfil',
    panel: 'Panel',
  },

  hero: {
    eyebrow: 'Café de especialidad — Villa Crespo',
    titleLine1: 'Precisión de laboratorio,',
    titleLine2: 'calidez de barrio.',
    seeMenu: 'Ver la carta',
    earnPoints: 'Sumar puntos',
    imageAlt: 'Un espresso recién servido en la barra de Caffeina',
  },

  concept: {
    eyebrow: 'Concepto',
    title: 'El nombre es la molécula.',
    body:
      'Café de especialidad tratado con precisión de laboratorio y calidez de barrio. La marca se comporta como la cafeína misma: simple, exacta, con efecto. Minimalismo de inspiración japonesa, con temperamento porteño en la voz.',
    imageAlt: 'Latte art servido en taza de cerámica',
  },

  menu: {
    eyebrow: 'La carta',
    address: 'Bonpland 943, Villa Crespo',
    failedBefore: 'No pudimos cargar la carta en este momento. Escribinos a ',
    failedAfter: ' o pasá por Bonpland 943.',
  },

  story: {
    eyebrow: 'Historia',
    title: 'Un lugar, una idea simple.',
    body:
      'Arrancamos en Villa Crespo con una regla madre: sacar un elemento antes que agregar uno. Medimos cada gramo y cada mililitro, pero el chiste vive en el mostrador, no en el diseño.',
    imageAlt: 'Preparación de café en método filtrado',
  },

  location: {
    eyebrow: 'Ubicación',
    address: 'Bonpland 943, Villa Crespo — Buenos Aires',
    hours: 'Lun a vie 8:30 a 19:30 · Sáb 9 a 20 · Dom cerrado',
    directions: 'Cómo llegar',
    mapTitle: 'Mapa — Caffeina, Bonpland 943, Villa Crespo',
  },

  cta: {
    title: 'Sumá un sello en cada visita',
    button: 'Sumar puntos',
  },

  login: {
    eyebrow: 'Sumá puntos en cada visita',
    title: 'Entrá y juntá sellos',
    noneConfigured:
      'Todavía no hay métodos de ingreso configurados. Si sos del equipo, cargá las credenciales del proveedor en el servidor.',
    continueWith: (provider: string) => `Continuar con ${provider}`,
    soon: '(Pronto)',
    footnoteLine1: 'Un solo toque, sin contraseñas.',
    footnoteLine2: 'Al continuar aceptás sumar sellos como buen cliente.',
    backToSite: '← Volver al sitio',
    otherMethod: '← Otro método',
    enter: 'Entrar',
    verifying: 'Verificando…',
    sending: 'Enviando…',

    emailLabel: 'Tu email',
    emailPlaceholder: 'vos@ejemplo.com',
    sendLink: 'Mandame el enlace',
    emailSent: 'Te mandamos un enlace y un código por email',
    emailCodeHint:
      'Tocá el enlace del email para entrar directo, o escribí el código de acá abajo.',
    changeEmail: '← Cambiar email',

    phoneLabel: 'Tu número con código de país',
    sendCode: 'Mandame el código',
    codeSent: 'Te mandamos un código por WhatsApp',
    changePhone: '← Cambiar número',

    codeLabel: 'Código de 6 dígitos',

    breakGlassLink: '¿Sos del equipo y no te llega el email?',
    breakGlassIntro:
      'Ingreso de emergencia para el equipo. Necesitás tu email de admin y el código que está guardado en el servidor.',
    breakGlassEmail: 'Tu email de admin',
    breakGlassCode: 'Código de emergencia',
    showCode: 'Mostrar código',
    hideCode: 'Ocultar código',
    codeFormatHint: 'No importan mayúsculas, espacios ni guiones.',

    errorSendCode: 'No pudimos enviar el código',
    errorVerifyCode: 'No pudimos verificar el código',
    errorSendEmail: 'No pudimos enviar el email',
    errorSignIn: 'No pudimos completar el ingreso',
  },

  /** Errors the OAuth callback hands back in the query string. */
  loginErrors: {
    cancelado: 'Cancelaste el ingreso',
    estado_invalido: 'La sesión de ingreso venció, probá de nuevo',
    respuesta_incompleta: 'El proveedor no devolvió los datos necesarios',
    fallo_el_login: 'No pudimos completar el ingreso',
    proveedor_desconocido: 'Ese método de ingreso no existe',
    google_no_configurado: 'Google todavía no está configurado',
    facebook_no_configurado: 'Facebook todavía no está configurado',
    instagram_no_configurado: 'Instagram todavía no está configurado',
  },

  confirm: {
    title: 'Confirmá tu ingreso',
    brokenTitle: 'Enlace incompleto',
    tapToEnter: 'Tocá el botón para entrar a tu cartilla.',
    enter: 'Entrar a Caffeina',
    entering: 'Entrando…',
    broken:
      'Parece que el enlace se cortó al copiarlo. Pedí uno nuevo desde la pantalla de ingreso.',
    backToLogin: '← Volver al ingreso',
  },

  profile: {
    logout: 'Salir',
    hello: 'Hola',
    qrFailed: 'No pudimos generar tu código',
    yourCode: 'Tu código',
    qrAlt: (name: string) => `Código QR de ${name}`,
    qrCaption: 'Mostrá este código en caja para sumar un sello en cada compra.',
    available: 'Disponible',
    remaining: (n: number) => `Faltan ${n}`,
    activity: 'Tu actividad',
    stampAdded: (total: number) => `Sello sumado · ${total} sellos`,
    rewardRedeemed: (reward: string) => `${reward} canjeado`,
    rewardFallback: 'Premio',
    staffAccess: 'Acceso staff →',
  },

  card: {
    title: 'Cartilla',
    stamps: (n: number) => `${n} sellos`,
  },
};

/** Every other language must supply exactly these keys. */
export type Dictionary = typeof es;

export default es;
